/** Hex (#rgb / #rrggbb) → HSL components. Returns null for invalid input. */
export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const full = normalizeHex(hex);
  if (!full) return null;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function normalizeHex(hex: string): string | null {
  const m = hex.trim().replace(/^#/, '');
  const full = m.length === 3 ? [...m].map((c) => c + c).join('') : m;
  return /^[0-9a-f]{6}$/i.test(full) ? full : null;
}

/** Relative luminance (WCAG) of a hex colour — used to pick a readable foreground. */
export function luminance(hex: string): number {
  const full = normalizeHex(hex) ?? '000000';
  const [r = 0, g = 0, b = 0] = [0, 2, 4].map((i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Resolve an HSL-triple CSS token (e.g. `--primary`) to a concrete colour string usable by canvas libs. */
export function cssToken(name: string, alpha?: number): string {
  if (typeof window === 'undefined') return '#888888';
  const probe = document.createElement('span');
  probe.style.display = 'none';
  probe.style.color = alpha === undefined ? `hsl(var(--${name}))` : `hsl(var(--${name}) / ${alpha})`;
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved || '#888888';
}
